import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { MainService } from 'src/app/app.service';
import { PoSelectOption } from '@po-ui/ng-components';

/**
 * Resolve o id de área que o Smart Review grava em cada execução para a descrição
 * que o usuário reconhece.
 *
 * O Smart Review persiste apenas o id (`5e70e23ee419c2694478e5a8`) porque a área é
 * um cadastro do ESP, não dele — e a execução é append-only, então guardar a
 * descrição junto congelaria um nome que pode ser corrigido depois. A tradução é
 * do front, que já consome esse cadastro.
 *
 * O catálogo é buscado uma vez e compartilhado (`shareReplay`): o painel resolve o
 * filtro, a tabela e o CSV a partir do mesmo mapa.
 */
@Injectable()
export class CostsAreasService {
  private areas$?: Observable<Map<string, string>>;

  constructor(private mainService: MainService) {}

  /**
   * Mapa id → descrição. Falha de rede devolve mapa vazio em vez de erro: sem o
   * catálogo o painel ainda funciona mostrando os ids, e derrubar a tela inteira
   * por causa de um rótulo seria desproporcional.
   */
  areaNames(): Observable<Map<string, string>> {
    if (!this.areas$) {
      this.areas$ = this.mainService.getAreas().pipe(
        map(areas => new Map((areas ?? []).map(area => [area.id, area.area]))),
        catchError(() => of(new Map<string, string>())),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.areas$;
  }

  /** Opções do filtro de área, ordenadas por descrição. O valor é sempre o id. */
  static toOptions(names: Map<string, string>): PoSelectOption[] {
    return [...names.entries()]
      .map(([id, label]) => ({ value: id, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }

  /**
   * Descrição de uma chave de área vinda do breakdown. Preserva chaves especiais
   * (`(sem informação)`, `(outros N)`) e cai no próprio id quando o cadastro não
   * tem o registro — área removida do ESP ainda aparece no histórico de custo.
   */
  static label(key: string, names: Map<string, string>): string {
    if (!key || key.startsWith('(')) { return key; }
    return names.get(key) || key;
  }
}
